import cn from 'classnames';
import { CleaveOptions } from 'cleave.js/options';
import Cleave from 'cleave.js/react';
import uniqueId from 'lodash-es/uniqueId';
import * as React from 'react';
import { useImperativeHandle, useRef, useState } from 'react';
import { BaseInputProps } from '@tcl-argon-models/models';
import { mergeClasses, PRIMARY_STATUS_COLORS, SECONDARY_STATUS_COLORS, StatusVariant } from '@tcl-argon-utils/utils';
import { ActionPalette, BrandPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import './styles.scss';
import styles from './input.module.css';
import commonInputStyles from './commonInputStyles.module.css';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

// TODO I omitted the translate prop because of a new type error brought up by adding Refs.
// I want to see if omitting that prop 'fixes' the error, because the alternative is removing refs
export interface InputProps
  extends Omit<BaseInputProps, 'theme' | 'classes'>,
    Omit<HTMLInputProps, 'translate' | 'enterKeyHint'> {
  /** Force the input to appear focused. Useful for demos */
  forceFocusHighlight?: boolean;
  /** Insert an Icon into the input to the left of the text entry component */
  leftIcon?: React.ReactNode;
  /** An input mask, for controlling the auto-formatting of entered data. Uses Cleave.js, see their docs for more info. */
  mask?: CleaveOptions;
  /** Insert an Icon into the input to the right of the text entry component */
  rightIcon?: React.ReactNode;
  /** input status will affect colors that reflect that status. 'error' | 'warning' | 'success' | 'default' */
  status?: StatusVariant;
  /** style will change on input if isPrimary is false. If isPrimary is true, default styling will be applied */
  isPrimary?: boolean;
  /** object to handle clearing action on input field */
  clearable?: React.ReactNode;
  classes?: Partial<typeof styles>;
}

export interface InputRefs {
  readonly input: HTMLInputElement;
  readonly label: HTMLLabelElement;
  readonly root: HTMLDivElement;
}

export const Input = React.forwardRef<InputRefs, InputProps>(
  (
    {
      className,
      classes: classOverrides = {},
      disabled = false,
      forceFocusHighlight = false,
      hideLabel = false,
      id,
      label,
      leftIcon,
      mask,
      onChange,
      rightIcon,
      subContent,
      status = 'default',
      isPrimary = true,
      clearable,
      value,
      ...inputProps
    },
    ref,
  ) => {
    [id] = useState(id || uniqueId('input_'));

    const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
    const labelRef = useRef<HTMLLabelElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      get input() {
        return inputRef.current as HTMLInputElement;
      },
      get label() {
        return labelRef.current as HTMLLabelElement;
      },
      get root() {
        return rootRef.current as HTMLInputElement;
      },
    }));

    React.useEffect(() => {
      if (
        forceFocusHighlight &&
        inputRef !== null &&
        inputRef !== undefined &&
        inputRef.current !== null &&
        inputRef.current !== undefined
      ) {
        inputRef.current.focus();
      }
    }, [forceFocusHighlight]);
    const clearInput = () => {
      if (inputRef !== null && inputRef !== undefined && inputRef.current !== null && inputRef.current !== undefined) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    };

    const getInputPadding = (): string => {
      let padding = '12px 16px';
      const paddingTop = '12px';
      const paddingBottom = '12px';
      const paddingLeft = '16px';
      const paddingRight = '16px';

      if (leftIcon && rightIcon) {
        padding = [paddingTop, '50px'].join(' ');
      } else if (leftIcon) {
        padding = [paddingTop, paddingRight, paddingBottom, '50px'].join(' ');
      } else if (rightIcon || clearable) {
        padding = [paddingTop, '50px', paddingBottom, paddingLeft].join(' ');
      }

      return padding;
    };

    const classes = mergeClasses(styles, commonInputStyles, classOverrides);

    return (
      <div ref={rootRef} className={cn(classes.root, className)} key={id}>
        <label
          ref={labelRef}
          className={cn(classes.label, { [classes.isVisuallyHidden]: hideLabel })}
          htmlFor={id}
          key={uniqueId('base_input_label_')}
          style={{ '--tcl-label-font-color': BrandPalette[700] } as React.CSSProperties}
        >
          {label}
        </label>
        <div
          className={cn(classes.inputWrapper, {
            [classes.disabled]: disabled,
            [classes.forceFocus]: forceFocusHighlight,
          })}
          style={
            {
              '--tcl-border-radius': isPrimary ? '20px' : '3px',
              '--tcl-background-color': SECONDARY_STATUS_COLORS[status],
              '--tcl-border-color':
                status !== 'default' ? PRIMARY_STATUS_COLORS[status] : isPrimary ? 'transparent' : ActionPalette[400],
              '--tcl-box-shadow':
                isPrimary && status === 'default' ? `0px 1px 3px 1px ${GrayscalePalette[300]}` : 'none',
              '--tcl-hover-border-color': ActionPalette[500],
              '--tcl-hover-background-color': SECONDARY_STATUS_COLORS[status],
              '--tcl-focus-background-color':
                status === 'default' ? GrayscalePalette[200] : SECONDARY_STATUS_COLORS[status],
              '--tcl-force-focus-border-color': GrayscalePalette[500],
              '--tcl-force-focus-background-color':
                status === 'default' ? GrayscalePalette[200] : SECONDARY_STATUS_COLORS[status],
            } as React.CSSProperties
          }
        >
          {leftIcon && <div className={classes.leftIcon}>{leftIcon}</div>}
          {mask == null ? (
            <input
              ref={inputRef}
              className={classes.input}
              disabled={disabled}
              id={id}
              onChange={onChange}
              value={value}
              {...inputProps}
              style={
                {
                  '--tcl-input-color': BrandPalette[700],
                  '--tcl-input-padding': getInputPadding(),
                  '--tcl-input-placeholder-color': GrayscalePalette[500],
                } as React.CSSProperties
              }
            />
          ) : (
            <Cleave
              htmlRef={(inputNode: HTMLInputElement) => {
                if (!inputRef) {
                  return;
                } else {
                  inputRef.current = inputNode;
                }
              }}
              className={classes.input}
              disabled={disabled}
              id={id}
              options={mask}
              onChange={onChange}
              value={value}
              style={
                {
                  '--tcl-input-color': BrandPalette[700],
                  '--tcl-input-padding': getInputPadding(),
                  '--tcl-input-placeholder-color': GrayscalePalette[500],
                } as React.CSSProperties
              }
              // Cleave should accept all props that <input/> elements accept, but the typing is borked.
              // This assertion forces compliance.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(inputProps as any)}
            />
          )}
          <div className={classes.rightIconClearable}>
            {clearable && value !== '' && value !== null && value !== undefined && (
              <div
                className={classes.clearable}
                onClick={clearInput}
                onKeyPress={clearInput}
                style={{ '--tcl-focus-border-color': ActionPalette[300] } as React.CSSProperties}
              >
                {clearable}
              </div>
            )}
            {clearable && rightIcon && value !== '' && value !== null && value !== undefined && (
              <div
                className={classes.divider}
                style={{ '--tcl-divider-border-color': ActionPalette[400] } as React.CSSProperties}
              ></div>
            )}
            {rightIcon && <div>{rightIcon}</div>}
          </div>
        </div>
        {subContent && (
          <p
            className={classes.subContent}
            style={
              {
                '--tcl-sub-content-font-color':
                  status === 'default' ? GrayscalePalette[500] : PRIMARY_STATUS_COLORS[status],
                '--tcl-link-color': ActionPalette[300],
              } as React.CSSProperties
            }
          >
            {subContent}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
